/**
 * DocumentsTab - Main container for document management
 *
 * Provides document upload, listing, processing, and preview functionality
 * for a transaction's documents.
 */

import { CheckCircle2, Loader2, Upload } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useDocuments, useUploadDocument } from '../../hooks';
import type { DocumentType } from '../../types';
import { DocumentListTable } from './DocumentListTable';
import { DocumentPreviewDialog } from './DocumentPreviewDialog';

export interface DocumentsTabProps {
  /** Transaction ID */
  transactionId: string;
  /** Optional className */
  className?: string;
}

export function DocumentsTab({ transactionId, className }: DocumentsTabProps) {
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const uploadMutation = useUploadDocument();

  const { data: _documents = [] } = useDocuments(transactionId);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      if (!file) return;

      // Validate file type
      if (file.type !== 'application/pdf') {
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        return;
      }

      setUploadSuccess(false);

      try {
        await uploadMutation.mutateAsync({
          transactionId,
          file,
          documentType: 'cd' as DocumentType,
          createVersion: false,
        });

        // Show success message
        setUploadSuccess(true);
        setTimeout(() => setUploadSuccess(false), 3000);
      } catch {
        // Error handled by mutation
      }
    },
    [transactionId, uploadMutation],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
    disabled: uploadMutation.isPending,
  });

  const handlePreview = useCallback((documentId: string) => {
    setPreviewDocumentId(documentId);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewDocumentId(null);
  }, []);

  return (
    <div className={className}>
      <div className="space-y-6">
        {/* Upload Section */}
        <div>
          <h2 className="text-xl font-semibold mb-1">Upload Document</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Upload a Closing Disclosure PDF to extract data using AI processing
          </p>

          <Card
            {...getRootProps()}
            className={`
              border-2 border-dashed transition-colors cursor-pointer
              ${
                isDragActive
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-primary/50'
              }
              ${uploadMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <CardContent className="p-8">
              <input {...getInputProps()} />
              {uploadMutation.isPending ? (
                <div className="text-center py-4">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
                  <p className="text-sm font-medium">Uploading document...</p>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm font-medium">
                    {isDragActive ? 'Drop the PDF here' : 'Drag & drop a PDF file here'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">or click to select a file</p>
                  <p className="text-xs text-muted-foreground mt-2">Maximum file size: 10 MB</p>
                </div>
              )}
            </CardContent>
          </Card>

          {uploadMutation.error && (
            <Alert variant="destructive" className="mt-4">
              <p className="text-sm">{uploadMutation.error.message}</p>
            </Alert>
          )}

          {uploadSuccess && (
            <Alert className="mt-4 border-green-200 bg-green-50 text-green-800">
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>Document uploaded successfully</AlertDescription>
            </Alert>
          )}
        </div>

        <Separator />

        {/* Documents List Section */}
        <div>
          <h2 className="text-xl font-semibold mb-1">Documents</h2>
          <p className="text-sm text-muted-foreground mb-4">
            View, process, and preview uploaded documents
          </p>

          <DocumentListTable transactionId={transactionId} onPreview={handlePreview} />
        </div>
      </div>

      {/* Preview Dialog */}
      <DocumentPreviewDialog
        open={previewDocumentId !== null}
        onOpenChange={handleClosePreview}
        transactionId={transactionId}
        documentId={previewDocumentId}
      />
    </div>
  );
}
